import { isEqual } from 'es-toolkit';
import { useAtom } from 'jotai';
import { SetStateAction, useCallback, useMemo } from 'react';

import { isSetStateFunction } from '@flipster/react/misc/state/is-set-state-function';
import { PersistStateAtom, createPersistStateAtom } from '@flipster/react/misc/state/persist-state.atom';
import { EventEmitter } from '@flipster/utils/event-emitter';
import { map } from '@flipster/utils/map';

import { LineToolState, ResolutionString, SeriesType, StudyInputValue } from '../../lib/charting_library';
import { ChartPriceType, defaultChartPriceType, normalizeChartPriceType } from './chart-price-type';
import { DefaultFavoriteIntervals, DefaultInterval } from './constants';

export interface QuickStudyConfig {
  label: string;
  enabled: boolean;
  inputs?: Record<string, StudyInputValue>;
}

export interface TradingViewData {
  template: Record<string, object>;
  chartType: SeriesType;
  priceType: ChartPriceType;
  favoriteIntervals: ResolutionString[];
  lineTools: Record<string, LineToolState[]>;
  quickStudies: Record<string, QuickStudyConfig>;
  positionsVisible: boolean;
  TPSLVisible: boolean;
  triggerOrderVisible: boolean;
  spotTriggerOrderVisible: boolean;
  limitOrderVisible: boolean;
  spotLimitOrderVisible: boolean;
  historyIndicatorVisible: boolean;
  whaleTrackerVisible: boolean;
  whaleTrackerRedDotVisible: boolean;
  interval: ResolutionString;
}

const defaultData: TradingViewData = {
  template: {},
  chartType: SeriesType.Candles,
  priceType: defaultChartPriceType,
  favoriteIntervals: DefaultFavoriteIntervals,
  lineTools: {},
  quickStudies: {},
  positionsVisible: true,
  TPSLVisible: true,
  triggerOrderVisible: true,
  spotTriggerOrderVisible: true,
  limitOrderVisible: true,
  spotLimitOrderVisible: true,
  historyIndicatorVisible: true,
  whaleTrackerVisible: true,
  whaleTrackerRedDotVisible: false,
  interval: DefaultInterval,
};

export class TradingViewDataManager {
  private static storagePrefix: Record<keyof TradingViewData, string> = {
    template: `flipster.tv.data.2.`,
    chartType: `flipster.tv.data.1.`,
    priceType: `flipster.tv.data.1.`,
    favoriteIntervals: `flipster.tv.data.1.`,
    lineTools: `flipster.tv.data.2.`,
    quickStudies: `flipster.tv.data.2.`,
    positionsVisible: `flipster.tv.data.1.`,
    TPSLVisible: `flipster.tv.data.1.`,
    triggerOrderVisible: `flipster.tv.data.1.`,
    spotTriggerOrderVisible: `flipster.tv.data.1.`,
    limitOrderVisible: `flipster.tv.data.1.`,
    spotLimitOrderVisible: `flipster.tv.data.1.`,
    historyIndicatorVisible: `flipster.tv.data.1.`,
    whaleTrackerVisible: `flipster.tv.data.1.`,
    whaleTrackerRedDotVisible: `flipster.tv.data.1.`,
    interval: `flipster.tv.data.1.`,
  };

  private static eventEmitter = new EventEmitter<TradingViewData>();

  private static get handler() {
    const webviewHandler = window.webkit?.messageHandlers?.StorageHandler ?? window.StorageHandler;
    const webHandler = window.localStorage;
    return {
      async getItem(key: keyof TradingViewData): Promise<string | null> {
        const storageKey = `${TradingViewDataManager.storagePrefix[key]}${key}`;
        if (webviewHandler) {
          return map(
            await webviewHandler?.postMessage(JSON.stringify({ action: 'get-item', key: storageKey })),
            String,
          );
        }
        return webHandler.getItem(storageKey);
      },
      setItem(key: keyof TradingViewData, value: string) {
        const storageKey = `${TradingViewDataManager.storagePrefix[key]}${key}`;
        if (webviewHandler) {
          webviewHandler?.postMessage(JSON.stringify({ action: 'set-item', key: storageKey, value }));
        } else {
          webHandler.setItem(storageKey, value);
        }
      },
      removeItem(key: keyof TradingViewData) {
        const storageKey = `${TradingViewDataManager.storagePrefix[key]}${key}`;
        if (webviewHandler) {
          webviewHandler?.postMessage(JSON.stringify({ action: 'remove-item', key: storageKey }));
        } else {
          webHandler.removeItem(storageKey);
        }
      },
    };
  }

  static subscribe<K extends keyof TradingViewData>(key: K, callback: (value: TradingViewData[K]) => void) {
    const webviewHandler = window.webkit?.messageHandlers?.StorageHandler ?? window.StorageHandler;
    if (webviewHandler != null) {
      return this.eventEmitter.on(key, callback);
    }

    const storageEventCallback = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === `${TradingViewDataManager.storagePrefix[key]}${key}`) {
        const newValue = (() => {
          const value = e.newValue;
          try {
            if (!value) return null;
            return JSON.parse(value);
          } catch {
            return value ?? null;
          }
        })();
        callback(newValue as never);
      }
    };
    window.addEventListener('storage', storageEventCallback);
    return () => {
      window.removeEventListener('storage', storageEventCallback);
    };
  }

  static async getItem<T extends keyof TradingViewData>(key: T): Promise<TradingViewData[T]> {
    try {
      const result = map(await this.handler.getItem(key), JSON.parse) as TradingViewData[T] | undefined;
      const value = result ?? defaultData[key];
      if (key === 'priceType') {
        return normalizeChartPriceType(value) as TradingViewData[T];
      }
      return value;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Unexpected TradingView data received', key, e);
      return defaultData[key];
    }
  }

  static setItem<T extends keyof TradingViewData>(key: T, value: TradingViewData[T]) {
    this.handler.setItem(key, JSON.stringify(value));
    this.eventEmitter.emit(key, value);
  }

  static removeItem<T extends keyof TradingViewData>(key: T) {
    this.handler.removeItem(key);
    this.eventEmitter.emit(key, defaultData[key]);
  }

  static normalizeItem(value: unknown) {
    return JSON.parse(JSON.stringify(value));
  }

  static migrate() {
    if (typeof localStorage === 'undefined') return;

    const chartType = map(localStorage.getItem('flipster.tv.chart-type'), Number);
    if (chartType != null) this.setItem('chartType', chartType);
    localStorage.removeItem('flipster.tv.chart-type');

    const priceType = localStorage.getItem('flipster.tv.price-type');
    if (priceType != null) {
      this.setItem('priceType', normalizeChartPriceType(priceType));
    }
    localStorage.removeItem('flipster.tv.price-type');

    try {
      const favoriteIntervals = map(localStorage.getItem('flipster.tv.favorite-intervals'), JSON.parse);
      if (favoriteIntervals && Array.isArray(favoriteIntervals)) {
        this.setItem('favoriteIntervals', favoriteIntervals as ResolutionString[]);
      }
      localStorage.removeItem('flipster.tv.favorite-intervals');
    } catch {
      // ignore
    }

    try {
      const positionsVisible = map(localStorage.getItem('flipster.tv.positions.visible'), JSON.parse);
      if (positionsVisible != null) this.setItem('positionsVisible', positionsVisible !== false);
      localStorage.removeItem('flipster.tv.positions.visible');
    } catch {
      // ignore
    }

    try {
      const TPSLVisible = map(localStorage.getItem('flipster.tv.tpsl.visible'), JSON.parse);
      if (TPSLVisible != null) this.setItem('TPSLVisible', TPSLVisible !== false);
      localStorage.removeItem('flipster.tv.tpsl.visible');
    } catch {
      // ignore
    }

    try {
      const triggerOrderVisible = map(localStorage.getItem('flipster.tv.triggerOrder.visible'), JSON.parse);
      if (triggerOrderVisible != null) this.setItem('triggerOrderVisible', triggerOrderVisible !== false);
      localStorage.removeItem('flipster.tv.triggerOrder.visible');
    } catch {
      // ignore
    }

    try {
      const spotTriggerOrderVisible = map(localStorage.getItem('flipster.tv.spotTriggerOrder.visible'), JSON.parse);
      if (spotTriggerOrderVisible != null) this.setItem('spotTriggerOrderVisible', spotTriggerOrderVisible !== false);
      localStorage.removeItem('flipster.tv.spotTriggerOrder.visible');
    } catch {
      // ignore
    }

    try {
      const limitOrderVisible = map(localStorage.getItem('flipster.tv.limitOrderVisible.visible'), JSON.parse);
      if (limitOrderVisible != null) this.setItem('limitOrderVisible', limitOrderVisible !== false);
      localStorage.removeItem('flipster.tv.limitOrderVisible.visible');
    } catch {
      // ignore
    }

    try {
      const spotLimitOrderVisible = map(localStorage.getItem('flipster.tv.spotLimitOrderVisible.visible'), JSON.parse);
      if (spotLimitOrderVisible != null) this.setItem('spotLimitOrderVisible', spotLimitOrderVisible !== false);
      localStorage.removeItem('flipster.tv.spotLimitOrderVisible.visible');
    } catch {
      // ignore
    }

    try {
      const historyIndicatorVisible = map(localStorage.getItem('flipster.tv.historyIndicator.visible'), JSON.parse);
      if (historyIndicatorVisible != null) this.setItem('historyIndicatorVisible', historyIndicatorVisible !== false);
      localStorage.removeItem('flipster.tv.historyIndicator.visible');
    } catch {
      // ignore
    }

    try {
      const whaleTrackerVisible = map(localStorage.getItem('flipster.tv.whaleTracker.visible'), JSON.parse);
      if (whaleTrackerVisible != null) this.setItem('whaleTrackerVisible', whaleTrackerVisible !== false);
      localStorage.removeItem('flipster.tv.whaleTracker.visible');
    } catch {
      // ignore
    }

    try {
      const whaleTrackerRedDotVisible = map(localStorage.getItem('flipster.tv.whaleTracker.red-dot'), JSON.parse);
      if (whaleTrackerRedDotVisible != null)
        this.setItem('whaleTrackerRedDotVisible', Boolean(whaleTrackerRedDotVisible));
      localStorage.removeItem('flipster.tv.whaleTracker.red-dot');
    } catch {
      // ignore
    }

    // Migrate quickStudies to Record<string, QuickStudyConfig>
    try {
      const storageKey = `${this.storagePrefix.quickStudies}quickStudies`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          // Old per-symbol format: Record<symbol, QuickStudy[]>
          if (!Array.isArray(parsed) && Object.values(parsed).every((v) => Array.isArray(v))) {
            const allStudies = Object.values(parsed).flat() as {
              name: string;
              label: string;
              inputs?: Record<string, StudyInputValue>;
            }[];
            const result = allStudies.reduce<Record<string, QuickStudyConfig>>((acc, study) => {
              const alreadyExists = Boolean(acc[study.name]);
              if (!alreadyExists) acc[study.name] = { label: study.label, enabled: true, inputs: study.inputs };
              return acc;
            }, {});
            this.setItem('quickStudies', result);
          }
          // Old global array format: QuickStudy[]
          else if (Array.isArray(parsed)) {
            const result = (
              parsed as { name: string; label: string; inputs?: Record<string, StudyInputValue> }[]
            ).reduce<Record<string, QuickStudyConfig>>((acc, study) => {
              const alreadyExists = Boolean(acc[study.name]);
              if (!alreadyExists) acc[study.name] = { label: study.label, enabled: true, inputs: study.inputs };
              return acc;
            }, {});
            this.setItem('quickStudies', result);
          }
        }
      }
    } catch {
      this.setItem('quickStudies', {});
    }

    // Force ON once for chart display options the user has never explicitly turned off.
    // Materializes the value so future default changes don't silently flip it back.
    try {
      const flagKey = 'flipster.tv.force-on-applied.1';
      if (localStorage.getItem(flagKey) == null) {
        const forceOnKeys = [
          'positionsVisible',
          'TPSLVisible',
          'triggerOrderVisible',
          'limitOrderVisible',
          'historyIndicatorVisible',
        ] as const;
        for (const key of forceOnKeys) {
          const storageKey = `${this.storagePrefix[key]}${key}`;
          if (localStorage.getItem(storageKey) == null) {
            this.setItem(key, true);
          }
        }
        localStorage.setItem(flagKey, 'true');
      }
    } catch {
      // ignore
    }
  }
}

type DataKey = Extract<keyof TradingViewData, string>;
interface UsePersistStateOptions<K extends DataKey> {
  key: K;
}

const storageAtomCache: Record<string, PersistStateAtom<TradingViewData, keyof TradingViewData>> = {};
export function getTradingViewDataStorageAtom<K extends DataKey>(key: K) {
  storageAtomCache[key] ??= createPersistStateAtom(key, {
    delegate: TradingViewDataManager,
    defaultValue: defaultData[key],
  }) as unknown as PersistStateAtom<TradingViewData, keyof TradingViewData>;
  return storageAtomCache[key] as unknown as PersistStateAtom<TradingViewData, K>;
}

export function useTradingViewDataStorage<K extends DataKey>({ key }: UsePersistStateOptions<K>) {
  const stateAtom = getTradingViewDataStorageAtom(key);
  const [value, setValue] = useAtom(stateAtom);

  const setValueInternal = useCallback(
    (incoming: SetStateAction<TradingViewData[K]>) => {
      setValue((prev) => (isSetStateFunction(incoming) ? incoming(prev) : incoming));
    },
    [setValue],
  );

  const clear = useCallback(() => {
    TradingViewDataManager.removeItem(key);
    setValue(defaultData[key]);
  }, [key, setValue]);

  return useMemo(
    () =>
      [
        {
          get value() {
            TradingViewDataManager.getItem(key).then((nextValue) => {
              if (!isEqual(nextValue, value)) {
                setValue(nextValue);
              }
            });

            return value;
          },
          async asyncValue() {
            const nextValue = await TradingViewDataManager.getItem(key);
            if (!isEqual(nextValue, value)) {
              setValue(nextValue);
              return nextValue;
            }
            return value;
          },
        },
        setValueInternal,
        clear,
      ] as const,
    [clear, key, setValue, setValueInternal, value],
  );
}
